import React, { FC, useCallback, useState } from 'react';

import delv from 'dlv';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { centsToDollarString } from 'utils';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import LoadPrepaidCardModal from 'components/load-prepaid-card-modal';
import SelectPaymentMethod from 'components/payment-method-select';
import TextButton from 'components/text-button';
import { TextWithArrowButton } from 'components/text-with-arrow-button';
import { usePrepaidsPaymentMethods } from 'hooks/prepaid/use-prepaids-payment-methods';
import useDialogModal from 'hooks/use-dialog-modal';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import useApplePay from 'state/apple-pay/hooks/use-apple-pay';
import { useAuthContext } from 'state/auth';
import {
  TransactionType,
  getGooglePaymentsConfigs,
} from 'state/google-pay/get-google-payments-configs';
import useGooglePay from 'state/google-pay/hooks/use-google-pay';
import { useNetworkContext } from 'state/network';
import { usePaymentContext } from 'state/payment';
import { PREPAID_RELOAD_AMOUNTS } from 'state/payment/constants';
import useReloadtimCard from 'state/payment/hooks/use-reload-tim-card';
import { IApplePayDetails, IGooglePayDetails } from 'state/payment/types';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants/primitives';
import { getStoredPrepaidCard } from 'utils/encryption';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import logger from 'utils/logger';
import { isApplePay, isGooglePay } from 'utils/payment/native-payment';
import { routes } from 'utils/routing';

import { Container, FullLineSeparator, TextButtonsWrapper } from '../styled';

import Header from './Header';
import {
  ActionContainer,
  AmountContainer,
  ContentBlock,
  FinalActionContainer,
  LoadPrePaidDialogContainer,
  MoneyButton,
  NoConnectionHeading,
  SelectPaymentMethodWrapper,
  TimCardHeading,
  TimCardSubHeading,
} from './styled';
import TimCardScanSection from './tim-card-scan-section';
import { ILoyalty } from './types';

const INITIAL_LOAD_AMOUNT = 500;

const LoyaltyTimCard: FC<ILoyalty> = ({ onDismiss }) => {
  const { formatMessage } = useIntl();
  const {
    getPrepaidPaymentMethod,
    getBalanceFromPaymentMethods,
    loading,
    prepaidReloadPaymentMethodId: reloadPaymentMethodId,
    setPrepaidReloadPaymentMethodId: setReloadPaymentMethodId,
    openTransferBalanceDialog,
    getPaymentMethods,
  } = usePaymentContext();
  const {
    nonSelectedReloadPaymentMethods,
    reloadPaymentMethods,
    selectedReloadPaymentMethod,
  } = usePrepaidsPaymentMethods();
  const { reloadGiftCard } = useReloadtimCard();

  const auth = useAuthContext();
  const { formatCurrencyForUser } = useUIContext();
  const billingCountry = delv(auth, 'user.details.isoCountryCode') || ISOs.CAN;
  const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);
  const { user } = useAuthContext();
  const { connection } = useNetworkContext();
  const [reloadAmt, setReloadAmt] = useState(
    user?.details?.defaultReloadAmt || INITIAL_LOAD_AMOUNT
  );
  const [currentTotalTimBalance, setCurrentTotalTimBalance] = useState(INITIAL_LOAD_AMOUNT);
  const [forceSuccessDialog, setForceSuccessDialog] = useState(false);
  const navigate = useNavigate();

  // Load payment methods on unmount to avoid changing balance
  // while the user looks at it or pickup new balance if the
  // user just paid at the store
  useEffectOnUnmount(() => {
    getPaymentMethods();
  });

  const [LoadPrepaidCardDialog, openLoadPrepaidCardDialog] = useDialogModal<
    {},
    { loadAmount: number; forceSuccess?: boolean }
  >({
    Component: LoadPrepaidCardModal,
  });

  // Check to see if there is an existing prepaid card
  const prepaidPaymentMethod = getPrepaidPaymentMethod();

  const applePaySelected = isApplePay(reloadPaymentMethodId);
  const googlePaySelected = isGooglePay(reloadPaymentMethodId);

  let balance = '0.00';
  let centsBalance = 0;

  if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
    centsBalance = getBalanceFromPaymentMethods(prepaidPaymentMethod!);
    balance = centsToDollarString(centsBalance);
  }

  const handleReloadGiftCard = useCallback(
    async (input?: IApplePayDetails | IGooglePayDetails) => {
      const reloadResult = await reloadGiftCard(reloadAmt, centsBalance, input);

      if (typeof reloadResult === 'number') {
        setCurrentTotalTimBalance(reloadResult);
        setForceSuccessDialog(true);
        openLoadPrepaidCardDialog();
      }
    },
    [
      reloadGiftCard,
      centsBalance,
      setCurrentTotalTimBalance,
      setForceSuccessDialog,
      openLoadPrepaidCardDialog,
      reloadAmt,
    ]
  );

  const handleApplePay = ({ error, data }: { error?: string; data?: IApplePayDetails }): void => {
    if (data) {
      handleReloadGiftCard(data);
      return;
    }
    if (error) {
      logger.warn(error);
    }
  };

  const { requestApplePayPayment } = useApplePay({
    handleApplePay,
    prepaid: true,
  });

  const { requestGooglePayPayment } = useGooglePay({
    onCompleted: (data: IGooglePayDetails) => {
      if (data) {
        handleReloadGiftCard(data);
      }
    },
  });

  const handleReloadClick = useCallback(() => {
    if (applePaySelected) {
      return requestApplePayPayment({
        countryCode,
        currencyCode,
        total: reloadAmt,
      });
    }
    if (googlePaySelected) {
      // Get the prepaid gateway configs for google pay
      const { gateway, gatewayMerchantId } = getGooglePaymentsConfigs({
        transactionType: TransactionType.PREPAID,
        countryCode,
      });
      return requestGooglePayPayment({
        countryCode,
        currencyCode,
        total: reloadAmt,
        gatewayMerchantId,
        gateway,
      });
    }
    return handleReloadGiftCard();
  }, [
    applePaySelected,
    googlePaySelected,
    handleReloadGiftCard,
    requestApplePayPayment,
    requestGooglePayPayment,
    countryCode,
    currencyCode,
    reloadAmt,
  ]);

  const handleNavigate = useCallback(
    (e, route) => {
      navigate(route, { replace: true });
      onDismiss(e);
    },
    [navigate, onDismiss]
  );

  const reloadDisabled =
    !prepaidPaymentMethod ||
    !reloadAmt ||
    (!selectedReloadPaymentMethod && !applePaySelected && !googlePaySelected);

  const cachedPrepaidCard = getStoredPrepaidCard();

  const useLoadPrePaidDialog = !loading && !(prepaidPaymentMethod && prepaidPaymentMethod.prepaid);

  if (!connection.connected) {
    return (
      <>
        <NoConnectionHeading>
          {formatMessage({ id: 'unableToLoadTimCardNoInternet' })}
        </NoConnectionHeading>
        {cachedPrepaidCard && <TimCardScanSection barcode={cachedPrepaidCard} />}
      </>
    );
  }

  return (
    <>
      <LoadPrepaidCardDialog
        loadAmount={currentTotalTimBalance}
        forceSuccess={forceSuccessDialog}
      />
      {useLoadPrePaidDialog ? (
        <LoadPrePaidDialogContainer>
          <ContentBlock margin="6.5rem 0 0">
            <TimCardHeading>{formatMessage({ id: 'timCard' })}</TimCardHeading>
            <TimCardSubHeading>{formatMessage({ id: 'emptyStateTimCardCopy' })}</TimCardSubHeading>
            <ActionContainer>
              <ActionButton
                variant={ActionButtonVariants.INVERSE}
                fullWidth
                onClick={openLoadPrepaidCardDialog}
              >
                {formatMessage({ id: 'emptyStateTimCTACopy' })}
              </ActionButton>
              <TextButton onClick={openTransferBalanceDialog}>
                {formatMessage({ id: 'transferBalanceFromAnotherTimCard' })}
              </TextButton>
            </ActionContainer>
          </ContentBlock>
        </LoadPrePaidDialogContainer>
      ) : (
        <Container>
          <ContentBlock margin={`${primitive.$spacing2} 0 0`}>
            <Header balance={balance} />
            <FullLineSeparator margin={`0 -${primitive.$spacing5} ${primitive.$spacing6}`} />
            <TimCardScanSection
              barcode={prepaidPaymentMethod?.prepaid?.shuffledCardNumber ?? null}
            />
          </ContentBlock>

          <ContentBlock>
            <TimCardHeading>{formatMessage({ id: 'reloadYourCard' })}</TimCardHeading>
            {(!!reloadPaymentMethods.length || selectedReloadPaymentMethod) && (
              <AmountContainer>
                {PREPAID_RELOAD_AMOUNTS.map((amount: number) => (
                  <MoneyButton
                    key={amount}
                    aria-label={formatMessage(
                      { id: 'reloadCardWithAmount' },
                      {
                        amount: formatCurrencyForUser(amount),
                      }
                    )}
                    selected={reloadAmt === amount}
                    onClick={() => setReloadAmt(amount)}
                    aria-current={reloadAmt === amount}
                  >
                    <div>${centsToDollarString(amount, 0)}</div>
                  </MoneyButton>
                ))}
              </AmountContainer>
            )}
            <SelectPaymentMethodWrapper>
              <SelectPaymentMethod
                selectedMethod={selectedReloadPaymentMethod}
                nonSelectedMethods={nonSelectedReloadPaymentMethods}
                onOptionClick={setReloadPaymentMethodId}
                onNavigateClick={handleNavigate}
              />
            </SelectPaymentMethodWrapper>
            <FinalActionContainer>
              <ActionButton
                variant={ActionButtonVariants.PRIMARY}
                fullWidth
                onClick={handleReloadClick}
                isLoading={loading}
                disabled={reloadDisabled}
                loadingColor={Styles.color.primary}
                skipLogEvent
              >
                {formatMessage({ id: 'reload' })}
              </ActionButton>
            </FinalActionContainer>
            <TextButtonsWrapper>
              <FullLineSeparator margin={`0`} />
              <TextWithArrowButton
                onClick={() => handleNavigate('', routes.timCard)}
                messageId="myTimCardLink"
              />
              <FullLineSeparator margin={`0`} />
              <TextWithArrowButton
                onClick={openTransferBalanceDialog}
                messageId="transferBalance"
              />
              <FullLineSeparator margin={`0`} />
            </TextButtonsWrapper>
          </ContentBlock>
        </Container>
      )}
    </>
  );
};

export default LoyaltyTimCard;
